import type { ErrorInfo, ReactNode } from 'react';
import { Component } from 'react';
import {
  isChunkError,
  recoverFromChunkError,
} from '@/lib/utils/chunkErrorRecovery';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * When this value changes, a caught error is cleared and children re-render.
   * Pass the current route (e.g. pathname) so navigating away from an errored
   * page recovers automatically — without it the boundary stays on the
   * fallback until a full page reload.
   */
  resetKey?: unknown;
  /**
   * Called right before a caught error is cleared (via resetKey change or the
   * "Try again" button). The error itself was almost always a rejected router
   * loader / TanStack Query call — those cache the rejection, so simply
   * re-rendering children hits the same cached error again. Use this to
   * invalidate that cache (`router.invalidate()`, `queryClient` reset) so the
   * retry actually re-fetches instead of silently no-oping until a hard
   * reload.
   */
  onReset?: () => void;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  override componentDidUpdate(prevProps: Props) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.props.onReset?.();
      this.setState({ error: null });
    }
  }

  private handleRetry = () => {
    this.props.onReset?.();
    this.setState({ error: null });
  };

  override render() {
    if (this.state.error) {
      // A stale-deploy chunk 404 (new deploy removed the hash this route/lazy
      // vendor chunk was built with). Reload once instead of showing the
      // generic fallback — recoverFromChunkError() is deduped, so repeated
      // renders (e.g. StrictMode) don't loop reloads.
      if (
        isChunkError(this.state.error.message) &&
        recoverFromChunkError(this.state.error.message)
      ) {
        return null;
      }
      return (
        this.props.fallback ?? (
          <div className='flex flex-col items-center justify-center h-full gap-3 p-8 text-center'>
            <p className='text-sm font-medium text-destructive'>
              Something went wrong
            </p>
            <p className='text-xs text-muted-foreground font-mono break-all max-w-md'>
              {this.state.error.message}
            </p>
            <button
              type='button'
              onClick={this.handleRetry}
              className='text-xs underline text-muted-foreground hover:text-foreground'
            >
              Try again
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
